import { BiBox, BiPackage, BiTag } from "react-icons/bi";

type StockStatus = "available" | "out-of-stock" | "low-stock";

type ProductCardProps = {
  name: string;
  company: string;
  batchNo: string;
  expiryDate: string;
  available: number;
  imageUrl: string;
  status: StockStatus;
  unitPrice: string;
  packPrice: string;
  cartonPrice: string;
  unitsPerPack: number;
  packsPerCarton: number;
  onOrder?: () => void;
};

const statusStyles: Record<
  StockStatus,
  { label: string; badge: string; dot: string; disabled: boolean }
> = {
  available: {
    label: "Available",
    badge: "border-green-400 bg-green-50 text-green-500",
    dot: "bg-green-500",
    disabled: false,
  },
  "out-of-stock": {
    label: "Out of stock",
    badge: "border-red-400 bg-red-50 text-red-500",
    dot: "bg-red-500",
    disabled: true,
  },
  "low-stock": {
    label: "Low stock",
    badge: "border-orange-400 bg-orange-50 text-orange-400",
    dot: "bg-orange-400",
    disabled: false,
  },
};

const priceItems = [
  { key: "unitPrice", label: "Unit Price:", Icon: BiTag },
  { key: "packPrice", label: "Pack Price:", Icon: BiPackage },
  { key: "cartonPrice", label: "Carton Price:", Icon: BiBox },
] as const;

export default function OrderByProductSearch({
  name,
  company,
  batchNo,
  expiryDate,
  available,
  imageUrl,
  status,
  unitPrice,
  packPrice,
  cartonPrice,
  unitsPerPack,
  packsPerCarton,
  onOrder,
}: ProductCardProps) {
  const stock = statusStyles[status];
  const prices = { unitPrice, packPrice, cartonPrice };

  return (
    <div className="">
      <article className="w-full max-w-[386px] rounded bg-white px-4 py-5 shadow-[0_6px_18px_rgba(15,23,42,0.16)]">
        <header className="mb-5 flex items-start justify-between gap-4 border-b border-neutral-100 pb-2.5">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold leading-5 text-neutral-800">
              {name}
            </h2>
            <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              {company}
            </p>
          </div>

          <span
            className={`inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold ${stock.badge}`}
          >
            <span className={`h-2 w-2 rounded-full ${stock.dot}`} />
            {stock.label}
          </span>
        </header>

        <section className="mb-6 grid grid-cols-[1fr_112px] gap-4">
          <div className="space-y-3">
            <InfoBlock label="Batch No:" value={batchNo} />
            <InfoBlock label="Expiry Date:" value={expiryDate} />
            <InfoBlock
              label="Available:"
              value={`${available} ${available === 1 ? "Carton" : "Cartons"}`}
            />
          </div>

          <img
            src={imageUrl}
            alt={name}
            className="h-28 w-28 rounded object-cover"
          />
        </section>

        <section className="mb-4 grid grid-cols-3 rounded bg-violet-50/80 px-2 py-3">
          {priceItems.map(({ key, label, Icon }, index) => (
            <div
              key={key}
              className={`flex min-h-[70px] flex-col items-center justify-center px-2 text-center ${
                index === 0 ? "" : "border-l border-violet-300"
              }`}
            >
              <Icon
                className="mb-3 h-4 w-4 text-neutral-900"
                strokeWidth={1.8}
              />
              <p className="text-xs text-neutral-500">{label}</p>
              <p className="mt-2 text-sm font-bold text-neutral-950">
                {prices[key]}
              </p>
            </div>
          ))}
        </section>

        <section className="mb-7 grid h-14 grid-cols-2 rounded border border-neutral-200 bg-white">
          <div className="flex items-center justify-center text-center text-xs font-semibold text-neutral-800">
            {unitsPerPack} Units / Pack
          </div>
          <div className="flex items-center justify-center border-l border-neutral-200 text-center text-xs font-semibold text-neutral-800">
            {packsPerCarton} Packs / Carton
          </div>
        </section>

        <button
          type="button"
          disabled={stock.disabled}
          onClick={onOrder}
          className="h-11 w-full rounded bg-[#33145f] text-base font-medium text-white transition hover:bg-[#2a0f51] disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500"
        >
          Order
        </button>
      </article>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs leading-4 text-neutral-500">{label}</p>
      <p className="text-sm font-medium leading-4 text-neutral-800">{value}</p>
    </div>
  );
}
